import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { CalendarDays, Check, Coins, Globe, Search, X } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  clientReservations as reservationsApi,
  instructors as instructorsApi,
} from '@/api/endpoints'
import { EmptyState, Input, Select, Spinner } from '@/components/ui'
import DateInput from '@/components/ui/DateInput'
import ColumnPicker, { useVisibleColumns } from '@/components/ui/ColumnPicker'
import Pagination from '@/components/ui/Pagination'
import { SortableHeader, useOrdering } from '@/components/ui/SortableHeader'
import PaymentDetailsModal from '@/features/pricing/PaymentDetailsModal'
import { CenterFilter, useCenterFilter, useCenters } from '@/features/centers/useCenters'
import { useCodebooks } from '@/hooks/useBootstrap'
import {
  cn,
  formatDate,
  formatDateTime,
  formatMoney,
  formatTime,
  truncate,
} from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { ClientReservationRow } from '@/types/api'
import { PaymentType, ReserveStatus } from '@/types/api'
import { partlyPaidAmount, paymentStatusLabel } from '@/lib/paymentStatus'

/**
 * Všetky rezervácie na jednej obrazovke.
 *
 * Kalendár odpovedá na otázku „čo je v stredu o desiatej“. Túto obrazovku
 * treba na otázky, ktoré sa cezeň nedajú položiť: čo tento klient absolvoval
 * za sezónu, kto ešte nezaplatil, čo prišlo cez widget, ktoré lekcie nemajú
 * inštruktora. Preto zoznam s filtrami, nie mriežka.
 *
 * Filtre aj zoradenie robí server (`ClientReservationFilter`,
 * `ordering_fields`) - triediť stránku dvadsiatich riadkov v prehliadači by
 * zoradilo len tú stránku, čo je horšie než nezoradiť nič.
 */
export default function ReservationsPage() {
  const { t } = useTranslation()
  const books = useCodebooks()
  const goTo = useNavigate()
  const readOnly = !useAuth((s) => s.user)?.can_write

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [payments, setPayments] = useState<ClientReservationRow | null>(null)

  // Stredisko (`sql/040`) - stĺpec a filter len pri viacerých strediskách.
  const { multi: multiCenter } = useCenters()
  const [centerIds] = useCenterFilter()
  const columns = useMemo(
    () =>
      COLUMNS.filter((column) => multiCenter || column.key !== 'center').map((column) => ({
        ...column,
        label: t(column.labelKey),
      })),
    [t, multiCenter],
  )
  const { toggle: toggleColumn, isVisible, reset } = useVisibleColumns(
    'l2s:reservationColumns',
    columns,
    DEFAULT_COLUMNS,
  )
  const { ordering, toggle } = useOrdering('-start', 'l2s:reservationOrdering')

  const { data, isLoading } = useQuery({
    queryKey: ['reservation-list', filters, page, pageSize, ordering, centerIds.join(',')],
    queryFn: () =>
      reservationsApi.list({
        ...queryFrom(filters),
        page,
        limit: pageSize,
        ordering,
        centers: centerIds.length ? centerIds.join(',') : undefined,
      }),
    placeholderData: keepPreviousData,
  })

  // Zoznam inštruktorov do filtra. Zmazaní sú medzi nimi zámerne - ich lekcie
  // v histórii zostávajú a bez nich by sa nedali dohľadať.
  const { data: instructors } = useQuery({
    queryKey: ['instructors', 'filter'],
    queryFn: () => instructorsApi.list({ limit: 200, ordering: 'sname' }),
    staleTime: 5 * 60 * 1000,
  })

  const totalPages = Math.max(1, Math.ceil((data?.count ?? 0) / pageSize))

  function patch(next: Partial<Filters>) {
    setFilters((current) => ({ ...current, ...next }))
    setPage(1)
  }

  const dirty = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{t('reservations.title')}</h1>
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>

        <div className="relative ml-auto">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={filters.q}
            onChange={(e) => patch({ q: e.target.value })}
            placeholder={t('reservations.searchPlaceholder')}
            className="w-80 pl-8"
          />
        </div>

        <ColumnPicker
          columns={columns}
          isVisible={isVisible}
          onToggle={toggleColumn}
          onReset={reset}
        />
      </div>

      <div className="card flex flex-wrap items-end gap-2 p-2.5">
        {multiCenter ? (
          <Labelled label={t('centers.center')}>
            <CenterFilter className="w-52" />
          </Labelled>
        ) : null}
        <Labelled label={t('reservations.from')}>
          <DateInput
            className="w-40"
            value={filters.date_from}
            onChange={(date_from) => patch({ date_from })}
          />
        </Labelled>
        <Labelled label={t('reservations.to')}>
          <DateInput
            className="w-40"
            value={filters.date_to}
            onChange={(date_to) => patch({ date_to })}
          />
        </Labelled>

        <Labelled label={t('reservation.status')}>
          <Select
            className="w-36"
            value={filters.status_id}
            onChange={(e) => patch({ status_id: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            {[...books.statuses.values()].map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.key}
              </option>
            ))}
          </Select>
        </Labelled>

        <Labelled label={t('reservation.instructor')}>
          <Select
            className="w-48"
            value={filters.instructor_id}
            onChange={(e) => patch({ instructor_id: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            <option value="none">{t('reservations.withoutInstructor')}</option>
            {(instructors?.results ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.full_name}
              </option>
            ))}
          </Select>
        </Labelled>

        <Labelled label={t('reservation.course')}>
          <Select
            className="w-36"
            value={filters.course_id}
            onChange={(e) => patch({ course_id: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            {[...books.courses.values()].map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.key}
              </option>
            ))}
          </Select>
        </Labelled>

        <Labelled label={t('reservation.language')}>
          <Select
            className="w-28"
            value={filters.lang_id}
            onChange={(e) => patch({ lang_id: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            {[...books.languages.values()].map((l) => (
              <option key={l.id} value={l.id}>
                {l.key}
              </option>
            ))}
          </Select>
        </Labelled>

        <Labelled label={t('reservation.paid')}>
          <Select
            className="w-48"
            value={filters.paid}
            onChange={(e) => patch({ paid: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            <option value="false">{t('reservations.unpaidOnly')}</option>
            <option value="partly">{t('reservations.partlyOnly')}</option>
            <option value="true">{t('reservations.paidOnly')}</option>
          </Select>
        </Labelled>

        <Labelled label={t('reservations.origin')}>
          <Select
            className="w-36"
            value={filters.online}
            onChange={(e) => patch({ online: e.target.value })}
          >
            <option value="">{t('reservations.all')}</option>
            <option value="true">{t('reservations.onlineOnly')}</option>
            <option value="false">{t('reservations.deskOnly')}</option>
          </Select>
        </Labelled>

        {dirty ? (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setFilters(EMPTY_FILTERS)
              setPage(1)
            }}
          >
            <X size={14} />
            {t('reservations.clearFilters')}
          </button>
        ) : null}
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <>
            {/* Stĺpcov je osemnásť - tabuľka sa posúva vodorovne, namiesto aby
                sa text lámal a riadky boli rôzne vysoké. */}
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead>
                  <tr>
                    {columns
                      .filter((column) => isVisible(column.key))
                      .map((column) => (
                        <SortableHeader
                          key={column.key}
                          field={'sort' in column ? column.sort : undefined}
                          label={column.label}
                          ordering={ordering}
                          onSort={(field) => {
                            toggle(field)
                            setPage(1)
                          }}
                          align={NUMERIC.has(column.key) ? 'right' : 'left'}
                        />
                      ))}
                    <th className="w-16" />
                  </tr>
                </thead>
                <tbody>
                  {data?.results.map((row) => (
                    <ReservationRow
                      key={row.id}
                      row={row}
                      isVisible={isVisible}
                      onCalendar={() =>
                        goTo(`/calendar?start=${(row.start ?? '').slice(0, 10)}&days=1`)
                      }
                      onPayments={() => setPayments(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pages={totalPages}
              pageSize={pageSize}
              count={data?.count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </>
        )}
      </div>

      {payments ? (
        <PaymentDetailsModal
          reservation={payments}
          clientName={payments.client?.full_name ?? ''}
          readOnly={readOnly}
          onClose={() => setPayments(null)}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface Filters {
  q: string
  date_from: string
  date_to: string
  status_id: string
  instructor_id: string
  course_id: string
  lang_id: string
  paid: string
  online: string
}

const EMPTY_FILTERS: Filters = {
  q: '',
  date_from: '',
  date_to: '',
  status_id: '',
  instructor_id: '',
  course_id: '',
  lang_id: '',
  paid: '',
  online: '',
}

/**
 * Filtre → parametre dotazu. Prázdne sa vynechávajú, aby sa v adrese
 * neobjavovalo `status_id=` — django-filter to síce ignoruje, ale kľúč dotazu
 * v cache by sa tým menil a rovnaký zoznam by sa ťahal dvakrát.
 */
function queryFrom(filters: Filters): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value === '') continue
    // „bez inštruktora“ nie je id, je to otázka na prázdny stĺpec.
    if (key === 'instructor_id' && value === 'none') {
      params.unassigned = true
      continue
    }
    params[key] = value
  }
  return params
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="text-[11px] uppercase tracking-wide text-slate-500">
      <div className="mb-0.5">{label}</div>
      {children}
    </label>
  )
}

/** Poradie stĺpcov. Kľúče sú v `localStorage`, takže sa nedajú premenovať bez
 *  toho, aby sa ľuďom stratila ich voľba. */
const COLUMNS = [
  { key: 'date', labelKey: 'reservation.date', sort: 'start', fixed: true },
  { key: 'time', labelKey: 'reservations.time' },
  { key: 'duration', labelKey: 'reservations.duration' },
  { key: 'client', labelKey: 'reservation.client', sort: 'client__sname' },
  { key: 'tel', labelKey: 'reservation.phone' },
  { key: 'email', labelKey: 'reservation.email' },
  { key: 'instructor', labelKey: 'reservation.instructor' },
  { key: 'center', labelKey: 'centers.center', sort: 'center_position' },
  { key: 'course', labelKey: 'reservation.course' },
  { key: 'language', labelKey: 'reservation.language' },
  { key: 'level', labelKey: 'clients.level' },
  { key: 'persons', labelKey: 'reservation.persons', sort: 'numbers' },
  { key: 'status', labelKey: 'reservation.status' },
  { key: 'price', labelKey: 'reservation.price', sort: 'fees_total_annotated' },
  { key: 'paid', labelKey: 'reservation.paid', sort: 'client_paid' },
  { key: 'ref', labelKey: 'reservation.paymentRef', sort: 'payment_ref' },
  { key: 'order', labelKey: 'reservations.order' },
  { key: 'origin', labelKey: 'reservations.origin' },
  { key: 'note', labelKey: 'global.note' },
  { key: 'created', labelKey: 'clients.created', sort: 'created' },
] as const

type ColumnKey = (typeof COLUMNS)[number]['key']

/** Čo je vidieť, kým si to niekto neprepne. Zvyšok je za výberom stĺpcov. */
const DEFAULT_COLUMNS: ColumnKey[] = [
  'date', 'time', 'client', 'instructor', 'center', 'course', 'persons', 'status',
  'price', 'paid', 'ref',
]

const NUMERIC: ReadonlySet<ColumnKey> = new Set(['persons', 'price', 'order'])

function ReservationRow({
  row,
  isVisible,
  onCalendar,
  onPayments,
}: {
  row: ClientReservationRow
  isVisible: (key: ColumnKey) => boolean
  onCalendar: () => void
  onPayments: () => void
}) {
  const { t } = useTranslation()
  const books = useCodebooks()
  const { nameOf } = useCenters()

  const cells: Record<ColumnKey, ReactNode> = {
    center: nameOf(row.center_id) || '—',
    date: <span className="font-medium">{formatDate(row.start) || '—'}</span>,
    time: `${formatTime(row.start)}–${formatTime(row.end)}`,
    duration: `${row.duration_minutes} min`,
    client: row.client ? (
      <Link
        to={`/clients/${row.client.id}`}
        className="font-medium text-brand-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.client.full_name || `#${row.client.id}`}
      </Link>
    ) : (
      '—'
    ),
    tel: row.client?.tel || '—',
    email: <span className="text-slate-500">{row.client?.email || '—'}</span>,
    // Prázdne nie je „nikto“, ale riadok „bez inštruktora“ - lekcia čaká na
    // pridelenie a to je iný stav než chýbajúci údaj.
    instructor: row.instructor_name ?? (
      <span className="text-amber-700">{t('reservations.withoutInstructor')}</span>
    ),
    course: books.label('courses', row.course_id) || '—',
    language: books.label('languages', row.lang_id) || '—',
    level: books.label('levels', row.client?.levelid) || '—',
    persons: row.numbers,
    status: <StatusBadge statusId={row.status_id} />,
    price: formatMoney(row.fees_total),
    paid: <PaidCell row={row} />,
    ref: row.payment_ref ? (
      <span className="font-mono text-[12px]">{row.payment_ref}</span>
    ) : (
      '—'
    ),
    order: row.order_size > 1 ? t('reservations.orderSize', { count: row.order_size }) : '—',
    origin: row.is_online ? (
      <span className="inline-flex items-center gap-1 text-slate-600">
        <Globe size={13} />
        {t('reservations.online')}
      </span>
    ) : (
      <span className="text-slate-400">{t('reservations.desk')}</span>
    ),
    note: row.note ? (
      <span className="block max-w-[18rem] truncate text-slate-500" title={row.note}>
        {truncate(row.note)}
      </span>
    ) : (
      '—'
    ),
    created: <span className="text-slate-400">{formatDateTime(row.created) || '—'}</span>,
  }

  return (
    <tr className="hover:bg-slate-50">
      {COLUMNS.filter((column) => isVisible(column.key)).map((column) => (
        <td
          key={column.key}
          className={cn(
            'whitespace-nowrap',
            NUMERIC.has(column.key) ? 'text-right tabular-nums' : undefined,
          )}
        >
          {cells[column.key]}
        </td>
      ))}
      <td className="whitespace-nowrap text-right">
        <button
          type="button"
          className="rounded p-1 text-slate-400 hover:bg-brand-50 hover:text-brand-600"
          title={t('reservations.openPayments')}
          onClick={onPayments}
        >
          <Coins size={15} />
        </button>
        <button
          type="button"
          className="rounded p-1 text-slate-400 hover:bg-brand-50 hover:text-brand-600"
          title={t('reservations.openCalendar')}
          onClick={onCalendar}
        >
          <CalendarDays size={15} />
        </button>
      </td>
    </tr>
  )
}

function StatusBadge({ statusId }: { statusId: number | null }) {
  const books = useCodebooks()
  const label = books.label('statuses', statusId) ?? '—'

  const tone =
    statusId === ReserveStatus.Confirmed
      ? 'bg-emerald-100 text-emerald-800'
      : statusId === ReserveStatus.Cancelled
        ? 'bg-slate-200 text-slate-600 line-through'
        : 'bg-amber-100 text-amber-800'

  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', tone)}>
      {label}
    </span>
  )
}

function PaidCell({ row }: { row: ClientReservationRow }) {
  const { t } = useTranslation()
  if (row.client_paid === PaymentType.Unpaid) {
    // Čiastočne uhradená sa od nezaplatenej odlíši farbou aj sumou.
    return (
      <span className={partlyPaidAmount(row) ? 'text-pink-700' : 'text-slate-400'}>
        {paymentStatusLabel(t, row)}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-emerald-700">
      <Check size={13} />
      {t(`reservation.payment.${row.client_paid}`)}
    </span>
  )
}
